import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { WithRealtimeConsumer } from '../mixins/with-realtime-consumer';
import { ApiFactory, IPublishStatus } from '../api/easypos-api';
import { ICategory, IProduct } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

// Sub-phase U.2a.2 — data-table list of menu items. The publish status
// row + Publish button + header actions stay; the per-item card grid is
// replaced with a sortable table. Category grouping (with section
// headers) dropped in favor of a sortable Category column — denser,
// works better with many categories.
@customElement('products-page')
export class MenuProductsPage extends Base {
  @state() private _items: IProduct[] = [];
  @state() private _categories: ICategory[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _publishStatus: IPublishStatus | null = null;
  @state() private _publishing = false;
  @state() private _publishToast: string | null = null;
  // Free-text search bound to <app-data-table>.freeTextFilter. Lives
  // in component state only — does NOT survive route change.
  @state() private _search = '';
  private _unsubMenuPublished: (() => void) | null = null;
  private _unsubReconnected: (() => void) | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        flex: none;
      }
      .publish-row {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
        flex: none;
      }
      .search-row {
        flex: none;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .search-row sl-input {
        flex: 1 1 auto;
        min-width: 0;
      }
      .search-row sl-input::part(input) {
        font-family: var(--font-mono);
        font-size: 0.9rem;
        letter-spacing: 0.01em;
      }
      .search-meta {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        letter-spacing: 0.06em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .publish-meta {
        font-size: 0.8rem;
        opacity: 0.65;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
      .money {
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  firstUpdated() {
    void this._initAsync();
  }

  private async _initAsync() {
    await this._load();
    this._wireRealtime();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) {
      void this._load();
      this._wireRealtime();
    }
    if (changed.has('realtime')) this._wireRealtime();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubMenuPublished?.();
    this._unsubMenuPublished = null;
    this._unsubReconnected?.();
    this._unsubReconnected = null;
  }

  // When any shop member publishes, refetch the full state — typically
  // the publish follows edits this tab hasn't seen yet. Also on
  // realtime reconnect (after a transient drop) we may have missed a
  // menuPublished event while offline; refetch unconditionally.
  private _wireRealtime() {
    this._unsubMenuPublished?.();
    this._unsubMenuPublished = null;
    this._unsubReconnected?.();
    this._unsubReconnected = null;
    if (!this.realtime) return;
    this._unsubMenuPublished = this.realtime.on('menuPublished', () => {
      void this._load();
    });
    this._unsubReconnected = this.realtime.onReconnected(() => {
      void this._load();
    });
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const [menu, cats, status] = await Promise.all([
        api.listMenu(this.activeShop.shopName),
        api.listCategories(this.activeShop.shopName),
        api.getPublishStatus(this.activeShop.shopName),
      ]);
      if (!menu.success) {
        this._error = menu.error ?? 'Failed to load menu.';
      } else {
        this._items = menu.data ?? [];
      }
      if (cats.success && cats.data) this._categories = cats.data;
      if (status.success && status.data) this._publishStatus = status.data;
    } finally {
      this._loading = false;
    }
  }

  private async _refreshPublishStatus() {
    if (!this.activeShop) return;
    const api = ApiFactory.authenticated();
    const status = await api.getPublishStatus(this.activeShop.shopName);
    if (status.success && status.data) this._publishStatus = status.data;
  }

  private async _publish() {
    if (!this.activeShop || this._publishing) return;
    this._publishing = true;
    this._publishToast = null;
    try {
      const api = ApiFactory.authenticated();
      const result = await api.publishMenu(this.activeShop.shopName);
      if (!result.success) {
        this._publishToast = result.error ?? 'Publish failed.';
        return;
      }
      const wasNoOp = result.data?.wasNoOp ?? false;
      this._publishToast = wasNoOp
        ? 'Already up to date — no changes since the last publish.'
        : `Published v${result.data?.pointer.version ?? '?'}.`;
      await this._refreshPublishStatus();
    } finally {
      this._publishing = false;
    }
  }

  private _publishPill() {
    const status = this._publishStatus;
    if (!status) return nothing;
    if (!status.hasEverPublished) {
      return html`<sl-badge variant="warning" pill>Never published</sl-badge>`;
    }
    if (status.publishRequired) {
      return html`<sl-badge variant="warning" pill>Publish required</sl-badge>`;
    }
    return html`<sl-badge variant="success" pill>Up to date</sl-badge>`;
  }

  private _publishMeta() {
    const s = this._publishStatus;
    if (!s) return null;
    const fmt = (iso?: string | null) => {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    };
    return `Edited ${fmt(s.lastEditedUtc)} · Published ${fmt(s.lastPublishedUtc)}`;
  }

  private _open(item: IProduct) {
    router.navigate(
      resolveRouterPath(`products/${encodeURIComponent(item.id)}`)
    );
  }

  private _new() {
    router.navigate(resolveRouterPath('products/new'));
  }

  private _categoryName(id: string | null): string {
    if (!id) return 'Uncategorised';
    return this._categories.find((c) => c.id === id)?.name ?? id;
  }

  // The cheapest active cell's price, used for the list's price column +
  // sort. A dimensioned product reads as "from ₱X".
  private _minPrice(item: IProduct): number {
    const cells = item.cells ?? [];
    if (cells.length === 0) return 0;
    return Math.min(...cells.map((c) => c.price));
  }

  // Derive the at-a-glance kind from the cell shape: a product with
  // variant dimensions is a "variant"; otherwise the (single) cell's
  // recipe shape classifies it — combo (product legs), resold (one stock
  // line), recipe (multi-line), or flat (empty recipe, sell on price).
  private _kindBadge(item: IProduct) {
    if ((item.variantDimensions?.length ?? 0) > 0) {
      return html`<sl-badge variant="primary" pill>variant</sl-badge>`;
    }
    const recipe = item.cells?.[0]?.recipe ?? [];
    if (recipe.some((l) => l.productId)) {
      return html`<sl-badge variant="warning" pill>combo</sl-badge>`;
    }
    if (recipe.length === 0) {
      return html`<sl-badge variant="neutral" pill>flat</sl-badge>`;
    }
    if (recipe.length === 1) {
      return html`<sl-badge variant="primary" pill>resold</sl-badge>`;
    }
    return html`<sl-badge variant="neutral" pill>recipe</sl-badge>`;
  }

  private get _dataProvider() {
    return createArrayDataProvider<IProduct>(() => this._items, {
      filterFn: (item, text) =>
        item.name.toLowerCase().includes(text) ||
        item.id.toLowerCase().includes(text) ||
        this._categoryName(item.categoryId).toLowerCase().includes(text),
    });
  }

  private _columns(): DataTableColumnDef[] {
    return [
      {
        title: '',
        fieldName: 'isActive',
        width: '36px',
        align: 'center',
        displayValue: (_t, item: IProduct) =>
          item.isActive
            ? html`<sl-badge
                variant="success"
                pill
                style="--size: 6px;"
              ></sl-badge>`
            : html`<sl-badge
                variant="neutral"
                pill
                style="--size: 6px;"
              ></sl-badge>`,
      },
      {
        title: 'Name',
        fieldName: 'name',
        sortable: true,
        minWidth: '180px',
        displayValue: (_t, item: IProduct) => html`
          <div>
            <strong>${item.name}</strong>
            <div style="font-size: 12px; opacity: 0.65;">${item.id}</div>
          </div>
        `,
      },
      {
        title: 'Category',
        fieldName: 'categoryId',
        sortable: true,
        width: '160px',
        displayValue: (_t, item: IProduct) =>
          item.categoryId
            ? this._categoryName(item.categoryId)
            : html`<span style="opacity: 0.5;"
                >${this._categoryName(null)}</span
              >`,
      },
      {
        title: 'Kind',
        fieldName: 'cells',
        width: '110px',
        displayValue: (_t, item: IProduct) => this._kindBadge(item),
      },
      {
        title: 'Price',
        fieldName: 'cells',
        width: '120px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IProduct) => {
          const min = this._minPrice(item);
          const multi = (item.cells?.length ?? 0) > 1;
          return multi ? `from ${min.toFixed(2)}` : min.toFixed(2);
        },
      },
      {
        title: 'Options',
        fieldName: 'modifierGroups',
        width: '80px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IProduct) => {
          const n = item.modifierGroups?.length ?? 0;
          return n > 0
            ? String(n)
            : html`<span style="opacity: 0.55;">—</span>`;
        },
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Menu</h2>
        ${
          isOwner
            ? html`
                <div style="display: flex; gap: 6px;">
                  <sl-button
                    size="small"
                    variant="primary"
                    @click=${() => this._new()}
                  >
                    <sl-icon slot="prefix" name="plus-circle"></sl-icon>
                    New product
                  </sl-button>
                  <sl-button
                    size="small"
                    variant="text"
                    @click=${() => router.navigate(resolveRouterPath('categories'))}
                  >
                    Categories
                  </sl-button>
                </div>
              `
            : nothing
        }
      </header>

      ${
        this._publishStatus
          ? html`
              <div class="publish-row">
                ${this._publishPill()}
                <span class="publish-meta">${this._publishMeta()}</span>
                ${
                  isOwner
                    ? html`
                        <sl-button
                          size="small"
                          variant=${
                            this._publishStatus.publishRequired ||
                            !this._publishStatus.hasEverPublished
                              ? 'primary'
                              : 'default'
                          }
                          ?loading=${this._publishing}
                          @click=${() => this._publish()}
                        >
                          <sl-icon slot="prefix" name="cloud-upload"></sl-icon>
                          Publish menu
                        </sl-button>
                      `
                    : nothing
                }
              </div>
            `
          : nothing
      }
      ${
        this._publishToast
          ? html`<sl-alert
              variant="primary"
              open
              closable
              @sl-after-hide=${() => (this._publishToast = null)}
            >
              ${this._publishToast}
            </sl-alert>`
          : nothing
      }
      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._items.length === 0
            ? html`
                <div class="empty">
                  <p>No menu items yet.</p>
                  ${
                    isOwner
                      ? html`<sl-button
                          variant="primary"
                          @click=${() => this._new()}
                          >Add your first item</sl-button
                        >`
                      : nothing
                  }
                </div>
              `
            : html`
                <div class="search-row">
                  <sl-input
                    placeholder="Search menu — name, category, ingredient…"
                    clearable
                    .value=${this._search}
                    @sl-input=${(e: Event) => {
                      this._search = (e.target as HTMLInputElement).value;
                    }}
                  >
                    <sl-icon slot="prefix" name="search"></sl-icon>
                  </sl-input>
                  ${
                    this._search
                      ? html`<span class="search-meta">FILTERING</span>`
                      : html`<span class="search-meta"
                          >${this._items.length} ITEMS</span
                        >`
                  }
                </div>
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns()}
                  .dataProvider=${this._dataProvider}
                  .freeTextFilter=${this._search}
                  .keyevent=${(item: IProduct) => item.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: IProduct }>) =>
                    this._open(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
